import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/Button';
import { AlertCircleIcon, CheckCircleIcon, KeyIcon, MailIcon, RefreshCwIcon, ShieldIcon, WifiOffIcon, DatabaseIcon, LockIcon, UserIcon, CookieIcon, ServerIcon, SparklesIcon } from 'lucide-react';
type TroubleshootingStep = 'initial' | 'account-check' | 'browser-check' | 'network-check' | 'solution';
export function LoginTroubleshooting() {
  const [currentStep, setCurrentStep] = useState<TroubleshootingStep>('initial');
  const [formData, setFormData] = useState({
    email: '',
    errorMessage: '',
    browser: '',
    operatingSystem: '',
    triedIncognito: 'no',
    usingVpn: 'no',
    clearedCache: 'no',
    disabledExtensions: 'no'
  });
  const [diagnosis, setDiagnosis] = useState<string[]>([]);
  const [solutions, setSolutions] = useState<string[]>([]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleNextStep = (nextStep: TroubleshootingStep) => {
    setCurrentStep(nextStep);
    window.scrollTo(0, 0);
  };
  const analyzeProblem = () => {
    const newDiagnosis: string[] = [];
    const newSolutions: string[] = [];
    // Check for common issues based on the form data
    if (formData.errorMessage.toLowerCase().includes('password')) {
      newDiagnosis.push('Incorrect password or account credentials');
      newSolutions.push('Reset your password using the "Forgot Password" link on the login page');
    }
    if (formData.errorMessage.toLowerCase().includes('not found') || formData.errorMessage.toLowerCase().includes('no account')) {
      newDiagnosis.push('Account may not exist with the provided email');
      newSolutions.push('Create a new account using the registration page');
      newSolutions.push('Double-check your email address for typos');
    }
    if (formData.usingVpn === 'yes') {
      newDiagnosis.push('VPN connection may be interfering with authentication');
      newSolutions.push('Temporarily disable your VPN and try logging in again');
    }
    if (formData.clearedCache === 'no') {
      newDiagnosis.push('Browser cache or cookies may be corrupted');
      newSolutions.push('Clear your browser cache and cookies, then try again');
    }
    if (formData.disabledExtensions === 'no') {
      newDiagnosis.push('Browser extensions might be blocking authentication');
      newSolutions.push('Temporarily disable all browser extensions and try again');
    }
    if (formData.triedIncognito === 'no') {
      newSolutions.push("Try logging in using your browser's incognito/private mode");
    }
    // If no specific issues identified, provide general solutions
    if (newDiagnosis.length === 0) {
      newDiagnosis.push('General authentication issue');
      newSolutions.push('Try logging in from a different device or browser');
      newSolutions.push('Check if the SponsrAI service is experiencing downtime');
      newSolutions.push('Contact support if the issue persists');
    }
    setDiagnosis(newDiagnosis);
    setSolutions(newSolutions);
    handleNextStep('solution');
  };
  return <Layout data-id="element-2433">
      <div className="min-h-screen w-full bg-white" data-id="element-2434">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-2435">
          {/* Header */}
          <div className="text-center mb-10 relative" data-id="element-2436">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -mt-6 w-32 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" data-id="element-2437"></div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center" data-id="element-2438">
              <KeyIcon className="h-6 w-6 text-indigo-600 mr-2" data-id="element-2439" />
              Login Troubleshooting
            </h1>
            <p className="text-gray-600" data-id="element-2440">
              Let's help you solve your login issues and get you back into your
              account.
            </p>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 mt-6 w-32 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" data-id="element-2441"></div>
          </div>
          {/* Main content - changes based on current step */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8 relative overflow-hidden" data-id="element-2442">
            {/* Mystical decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-50 rounded-full -mr-20 -mt-20 opacity-30" data-id="element-2443"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-50 rounded-full -ml-16 -mb-16 opacity-30" data-id="element-2444"></div>
            {/* Subtle arcane pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-100/10 via-transparent to-transparent opacity-40" data-id="element-2445"></div>
            <div className="relative z-10" data-id="element-2446">
              {currentStep === 'initial' && <div data-id="element-2447">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2448">
                    <SparklesIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2449" />
                    What's happening?
                  </h2>
                  <div className="space-y-6" data-id="element-2450">
                    <div data-id="element-2451">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2452">
                        Your Email Address
                      </label>
                      <div className="relative" data-id="element-2453">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2454">
                          <MailIcon className="h-5 w-5 text-gray-400" data-id="element-2455" />
                        </div>
                        <input type="email" name="email" className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Enter the email you're trying to use" value={formData.email} onChange={handleInputChange} data-id="element-2456" />
                      </div>
                    </div>
                    <div data-id="element-2457">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2458">
                        Error Message (if any)
                      </label>
                      <textarea name="errorMessage" rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Copy and paste any error message you see" value={formData.errorMessage} onChange={handleInputChange} data-id="element-2459"></textarea>
                    </div>
                    <div className="flex justify-end" data-id="element-2460">
                      <Button onClick={() => handleNextStep('account-check')} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2461">
                        Continue
                      </Button>
                    </div>
                  </div>
                </div>}
              {currentStep === 'account-check' && <div data-id="element-2462">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2463">
                    <UserIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2464" />
                    Account Check
                  </h2>
                  <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-100" data-id="element-2465">
                    <p className="text-sm text-indigo-700" data-id="element-2466">
                      Let's first check if we can find your account and verify
                      its status.
                    </p>
                  </div>
                  <div className="space-y-4 mb-6" data-id="element-2467">
                    <div className="flex items-start p-4 border border-gray-200 rounded-lg bg-white" data-id="element-2468">
                      <div className="flex-shrink-0 pt-0.5" data-id="element-2469">
                        <DatabaseIcon className="h-5 w-5 text-indigo-500" data-id="element-2470" />
                      </div>
                      <div className="ml-3" data-id="element-2471">
                        <h3 className="text-sm font-medium text-gray-900" data-id="element-2472">
                          Account Lookup
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-2473">
                          We'll check if an account exists with email:{' '}
                          <span className="font-medium text-gray-700" data-id="element-2474">
                            {formData.email || '[No email provided]'}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start p-4 border border-gray-200 rounded-lg bg-white" data-id="element-2475">
                      <div className="flex-shrink-0 pt-0.5" data-id="element-2476">
                        <LockIcon className="h-5 w-5 text-indigo-500" data-id="element-2477" />
                      </div>
                      <div className="ml-3" data-id="element-2478">
                        <h3 className="text-sm font-medium text-gray-900" data-id="element-2479">
                          Account Status
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-2480">
                          We'll verify if your account is active and not locked
                          due to too many failed login attempts.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 mb-6" data-id="element-2481">
                    <div data-id="element-2482">
                      <p className="text-sm text-gray-700 mb-2" data-id="element-2483">
                        Have you tried resetting your password?
                      </p>
                      <div className="flex space-x-4" data-id="element-2484">
                        <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => window.open('/login', '_blank')} data-id="element-2485">
                          Try Password Reset
                        </button>
                        <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => window.open('/register', '_blank')} data-id="element-2486">
                          Create New Account
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between" data-id="element-2487">
                    <Button onClick={() => handleNextStep('initial')} variant="outline" data-id="element-2488">
                      Back
                    </Button>
                    <Button onClick={() => handleNextStep('browser-check')} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2489">
                      Continue
                    </Button>
                  </div>
                </div>}
              {currentStep === 'browser-check' && <div data-id="element-2490">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2491">
                    <div className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2492" />
                    Browser & System Check
                  </h2>
                  <div className="space-y-6" data-id="element-2493">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2494">
                      <div data-id="element-2495">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2496">
                          Browser and Version
                        </label>
                        <input type="text" name="browser" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g. Chrome 94, Firefox 92" value={formData.browser} onChange={handleInputChange} data-id="element-2497" />
                      </div>
                      <div data-id="element-2498">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2499">
                          Operating System
                        </label>
                        <input type="text" name="operatingSystem" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g. Windows 10, macOS, iOS" value={formData.operatingSystem} onChange={handleInputChange} data-id="element-2500" />
                      </div>
                    </div>
                    <div className="space-y-4" data-id="element-2501">
                      <div data-id="element-2502">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2503">
                          Have you tried using incognito/private mode?
                        </label>
                        <div className="flex space-x-4" data-id="element-2504">
                          <label className="inline-flex items-center" data-id="element-2505">
                            <input type="radio" name="triedIncognito" value="yes" checked={formData.triedIncognito === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2506" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2507">
                              Yes
                            </span>
                          </label>
                          <label className="inline-flex items-center" data-id="element-2508">
                            <input type="radio" name="triedIncognito" value="no" checked={formData.triedIncognito === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2509" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2510">
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                      <div data-id="element-2511">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2512">
                          Have you cleared your browser cache and cookies?
                        </label>
                        <div className="flex space-x-4" data-id="element-2513">
                          <label className="inline-flex items-center" data-id="element-2514">
                            <input type="radio" name="clearedCache" value="yes" checked={formData.clearedCache === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2515" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2516">
                              Yes
                            </span>
                          </label>
                          <label className="inline-flex items-center" data-id="element-2517">
                            <input type="radio" name="clearedCache" value="no" checked={formData.clearedCache === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2518" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2519">
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                      <div data-id="element-2520">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2521">
                          Have you disabled browser extensions/add-ons?
                        </label>
                        <div className="flex space-x-4" data-id="element-2522">
                          <label className="inline-flex items-center" data-id="element-2523">
                            <input type="radio" name="disabledExtensions" value="yes" checked={formData.disabledExtensions === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2524" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2525">
                              Yes
                            </span>
                          </label>
                          <label className="inline-flex items-center" data-id="element-2526">
                            <input type="radio" name="disabledExtensions" value="no" checked={formData.disabledExtensions === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2527" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-2528">
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between" data-id="element-2529">
                      <Button onClick={() => handleNextStep('account-check')} variant="outline" data-id="element-2530">
                        Back
                      </Button>
                      <Button onClick={() => handleNextStep('network-check')} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2531">
                        Continue
                      </Button>
                    </div>
                  </div>
                </div>}
              {currentStep === 'network-check' && <div data-id="element-2532">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2533">
                    <WifiOffIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2534" />
                    Network & Connection Check
                  </h2>
                  <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-100" data-id="element-2535">
                    <p className="text-sm text-indigo-700" data-id="element-2536">
                      Sometimes network issues or security settings can prevent
                      successful login.
                    </p>
                  </div>
                  <div className="space-y-6" data-id="element-2537">
                    <div data-id="element-2538">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2539">
                        Are you using a VPN or corporate network?
                      </label>
                      <div className="flex space-x-4" data-id="element-2540">
                        <label className="inline-flex items-center" data-id="element-2541">
                          <input type="radio" name="usingVpn" value="yes" checked={formData.usingVpn === 'yes'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2542" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2543">
                            Yes
                          </span>
                        </label>
                        <label className="inline-flex items-center" data-id="element-2544">
                          <input type="radio" name="usingVpn" value="no" checked={formData.usingVpn === 'no'} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-2545" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2546">No</span>
                        </label>
                      </div>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg bg-white" data-id="element-2547">
                      <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2548">
                        Network Troubleshooting Tips
                      </h3>
                      <ul className="space-y-2 text-sm text-gray-600" data-id="element-2549">
                        <li className="flex items-start" data-id="element-2550">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2551">
                            •
                          </span>
                          <span data-id="element-2552">
                            Try using a different network (switch from WiFi to
                            mobile data)
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-2553">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2554">
                            •
                          </span>
                          <span data-id="element-2555">
                            Restart your router if you're on a home network
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-2556">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2557">
                            •
                          </span>
                          <span data-id="element-2558">
                            Check if your network blocks certain websites or
                            services
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-2559">
                          <span className="flex-shrink-0 h-5 w-5 text-indigo-500 flex items-center justify-center mr-1" data-id="element-2560">
                            •
                          </span>
                          <span data-id="element-2561">
                            Temporarily disable any firewall or security
                            software
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="flex justify-between" data-id="element-2562">
                      <Button onClick={() => handleNextStep('browser-check')} variant="outline" data-id="element-2563">
                        Back
                      </Button>
                      <Button onClick={analyzeProblem} variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2564">
                        Find Solutions
                      </Button>
                    </div>
                  </div>
                </div>}
              {currentStep === 'solution' && <div data-id="element-2565">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" data-id="element-2566">
                    <CheckCircleIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2567" />
                    Your Personalized Solutions
                  </h2>
                  <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-100" data-id="element-2568">
                    <p className="text-sm text-indigo-700" data-id="element-2569">
                      Based on your answers, we've identified potential
                      solutions to help you log in.
                    </p>
                  </div>
                  {diagnosis.length > 0 && <div className="mb-6" data-id="element-2570">
                      <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2571">
                        Potential Issues:
                      </h3>
                      <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2572">
                        {diagnosis.map((issue, index) => <div key={index} className={`p-3 flex items-start ${index !== diagnosis.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-2573">
                            <AlertCircleIcon className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-2574" />
                            <span className="text-sm text-gray-700" data-id="element-2575">
                              {issue}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                  <div className="mb-8" data-id="element-2576">
                    <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2577">
                      Recommended Solutions:
                    </h3>
                    <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2578">
                      {solutions.map((solution, index) => <div key={index} className={`p-3 flex items-start ${index !== solutions.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-2579">
                          <div className="flex-shrink-0 h-5 w-5 text-green-500 flex items-center justify-center mr-2 mt-0.5" data-id="element-2580">
                            {index + 1}
                          </div>
                          <span className="text-sm text-gray-700" data-id="element-2581">
                            {solution}
                          </span>
                        </div>)}
                    </div>
                  </div>
                  <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6" data-id="element-2582">
                    <div className="flex" data-id="element-2583">
                      <ShieldIcon className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0" data-id="element-2584" />
                      <div data-id="element-2585">
                        <h3 className="text-sm font-medium text-purple-900 mb-1" data-id="element-2586">
                          Still having trouble?
                        </h3>
                        <p className="text-sm text-purple-700" data-id="element-2587">
                          Our support team is ready to help. Click the chat icon
                          in the bottom right corner or email us at
                          support@sponsrai.se
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between" data-id="element-2588">
                    <Button onClick={() => handleNextStep('initial')} variant="outline" className="flex items-center" data-id="element-2589">
                      <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-2590" />
                      Start Over
                    </Button>
                    <Link to="/login" data-id="element-2591">
                      <Button variant="primary" className="bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-2592">
                        Return to Login
                      </Button>
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
          {/* Common login issues */}
          <div className="mt-12" data-id="element-2593">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center" data-id="element-2594">
              Common Login Issues
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2595">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 relative overflow-hidden" data-id="element-2596">
                <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-50 rounded-full -mr-8 -mt-8 opacity-30" data-id="element-2597"></div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center" data-id="element-2598">
                  <CookieIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-2599" />
                  Browser Storage Issues
                </h3>
                <p className="text-sm text-gray-600 mb-3" data-id="element-2600">
                  Our platform requires cookies and local storage to function
                  properly. Make sure:
                </p>
                <ul className="text-sm text-gray-600 space-y-2" data-id="element-2601">
                  <li className="flex items-start" data-id="element-2602">
                    <span className="text-indigo-500 mr-2" data-id="element-2603">•</span>
                    <span data-id="element-2604">Cookies are enabled in your browser</span>
                  </li>
                  <li className="flex items-start" data-id="element-2605">
                    <span className="text-indigo-500 mr-2" data-id="element-2606">•</span>
                    <span data-id="element-2607">You're not in private/incognito browsing mode</span>
                  </li>
                  <li className="flex items-start" data-id="element-2608">
                    <span className="text-indigo-500 mr-2" data-id="element-2609">•</span>
                    <span data-id="element-2610">No privacy extensions are blocking storage</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 relative overflow-hidden" data-id="element-2611">
                <div className="absolute top-0 right-0 w-16 h-16 bg-purple-50 rounded-full -mr-8 -mt-8 opacity-30" data-id="element-2612"></div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center" data-id="element-2613">
                  <ServerIcon className="h-5 w-5 text-purple-500 mr-2" data-id="element-2614" />
                  Account Security
                </h3>
                <p className="text-sm text-gray-600 mb-3" data-id="element-2615">
                  We take security seriously. Your account might be temporarily
                  locked if:
                </p>
                <ul className="text-sm text-gray-600 space-y-2" data-id="element-2616">
                  <li className="flex items-start" data-id="element-2617">
                    <span className="text-purple-500 mr-2" data-id="element-2618">•</span>
                    <span data-id="element-2619">There were too many failed login attempts</span>
                  </li>
                  <li className="flex items-start" data-id="element-2620">
                    <span className="text-purple-500 mr-2" data-id="element-2621">•</span>
                    <span data-id="element-2622">
                      Suspicious activity was detected from your account
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2623">
                    <span className="text-purple-500 mr-2" data-id="element-2624">•</span>
                    <span data-id="element-2625">You're logging in from an unusual location</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
}